import { useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Tabs } from "@mantine/core";
import { IconHistory, IconShieldCheck } from "@tabler/icons-react";
import { isRichTextEmpty } from "@congrs/utils";

import { AuthContext } from "../services/context";
import { getCurrentWorkspace } from "../services/userConfig";
import { useGetCompliancePolicy } from "../api/compliance";
import PolicyEditor from "../components/Compliance/PolicyEditor";
import ActivityLogTable from "../components/Compliance/ActivityLogTable";

export default function Compliance() {
  const { user } = useContext(AuthContext);
  const eventId = getCurrentWorkspace(user)?.eventId;

  const [searchParams, setSearchParams] = useSearchParams();
  const tabValue = searchParams.get("tab") || "editor";

  const { data: policy } = useGetCompliancePolicy({ eventId });

  // The page is only reachable with the flag on, so what is left to decide is
  // whether the organizer has actually written the text. An event provisioned
  // with the Event Foundation package arrives here with the flag on and nothing
  // written, and saying "live" before then would be a lie the organizer acts on.
  const isLive = !isRichTextEmpty(policy?.body);

  return (
    <Tabs
      keepMounted={false}
      variant="outline"
      value={tabValue}
      onChange={(tab) => setSearchParams({ tab })}
    >
      <Tabs.List>
        <Tabs.Tab value="editor" leftSection={<IconShieldCheck size={12} />}>
          Editor
        </Tabs.Tab>
        <Tabs.Tab value="activity" leftSection={<IconHistory size={12} />}>
          Activity log
        </Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="editor" pt="md">
        <PolicyEditor eventId={eventId} isLive={isLive} />
      </Tabs.Panel>

      <Tabs.Panel value="activity" pt="md">
        {isLive ? (
          <ActivityLogTable eventId={eventId} />
        ) : (
          // Nothing can have happened before the gate ran. An empty log reads
          // as "the audience ignored us" when it only means there was never
          // anything to answer.
          <Alert color="blue" variant="light">
            No activity yet — the gate is not live. Write the compliance text
            under Editor, and attendees will be asked the next time they enter
            the event.
          </Alert>
        )}
      </Tabs.Panel>
    </Tabs>
  );
}
